import { MouseEventHandler, ReactNode, useContext, useEffect, useState } from 'react';
import { useId } from '@floating-ui/react-dom-interactions';
import { theme } from '@frontend/theme';
import { styles } from '../../../../styles';
import { Placement } from '../../../hint/get-position';
import { Text } from '../../../text';
import { useTooltip } from '../../../tooltip';
import { SelectContext } from './dropdown-field.context';
import { DropdownFieldStyles } from './styles';

export interface DropdownListItemProps {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  isSelectable?: boolean;
  tooltipPlacement?: Placement;
  /** String for searching against (if children is jsx or varies from the value) */
  searchValue?: string;
  trackingId?: string;
  displayValue?: string;
  value: string;
  onClick?: MouseEventHandler;
}

/**
 *
 * After some of the new floating-ui components have baked and garnered trust,
 * this component should be genericized and merged with
 * `libs/ui/design-system/src/components/forms/fields/time-field/dropdown-list-item.tsx`
 */
export const DropdownListItem = ({
  children,
  disabled,
  isSelectable = true,
  searchValue,
  value,
  displayValue,
  tooltipPlacement,
  onClick,
  trackingId,
  ...rest
}: DropdownListItemProps) => {
  const {
    activeIndex,
    getItemProps,
    listItemsRef,
    lookupByValue,
    name,
    onChange,
    selectedIndex,
    setFloatingElementOpen,
    setSelectedIndex,
  } = useContext(SelectContext);
  const index = lookupByValue[value]?.index ?? 0;
  const id = useId();
  const selected = selectedIndex === index;
  const active = activeIndex === index;
  const dropdownOptionStyles = DropdownFieldStyles.dropdownOption({ active, disabled });
  const [shouldShowHoverLabel, setShouldShowHoverLabel] = useState<boolean>(false);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: tooltipPlacement || 'top',
  });
  const conditionalTriggerProps = shouldShowHoverLabel ? triggerProps : { ref: null };

  function composeRefs(node: HTMLLIElement) {
    listItemsRef.current[index] = node;
    if (conditionalTriggerProps.ref) {
      conditionalTriggerProps.ref(node);
    }
  }

  useEffect(() => {
    const listItem = listItemsRef.current?.[index];
    if (listItem) {
      const childElement = listItem.children[0] as HTMLSpanElement;
      setShouldShowHoverLabel(childElement.scrollWidth > childElement.offsetWidth);
    }
  }, [listItemsRef.current?.[index]?.children[0]?.scrollWidth, activeIndex]);

  const { key, ...itemProps } = getItemProps({
    ref: (node: HTMLLIElement) => {
      if (node) {
        listItemsRef.current[index] = node;
      }
    },
    // order of items will not change, so index is a reliable key
    key: index,
    onClick(e) {
      if (disabled) return;
      setSelectedIndex(index);
      onChange({ name, value });

      setFloatingElementOpen(false);

      if (typeof onClick === 'function') {
        onClick(e);
      }
    },
  });

  return (
    <li
      {...conditionalTriggerProps}
      aria-disabled={disabled}
      aria-selected={active}
      id={id}
      role='option'
      data-value={value}
      data-trackingid={trackingId}
      {...itemProps}
      ref={composeRefs}
      css={[
        dropdownOptionStyles,
        {
          position: 'relative',
        },
      ]}
      {...rest}
    >
      {isSelectable && selected && (
        <div
          data-testid='select-option-indicator'
          css={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '2px',
            height: '100%',
            backgroundColor: theme.colors.primary50,
          }}
        />
      )}
      <Text as='span' className='select-option-text' css={styles.truncate}>
        {children ?? <i>No value</i>}
      </Text>
      {!!children && shouldShowHoverLabel && <Tooltip {...tooltipProps}>{children}</Tooltip>}
    </li>
  );
};
